import { Entity } from '@api';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useCompanyCountryExposure } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '@terminal/routes/app-paths';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { GeographicExposureDrawer } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/GeographicExposureDrawer';
import { useGeographicDrawer } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/useGeographicDrawer';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router';
import { ConnectionsEmptyOrError } from '../shared/connections-error-or-empty/ConnectionsEmptyOrError';
import { RowItem } from '../shared/row-item/RowItem';
import styles from './GeographicExposureKnowledgeGraph.module.scss';

export interface GeographicExposureKnowledgeGraphProps {
  entity: Entity;
}

export const GeographicExposureKnowledgeGraph = ({
  entity,
}: GeographicExposureKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget', 'countries']);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    data: countryExposureData = [],
    isFetching,
    error,
  } = useCompanyCountryExposure({
    entityTag: entity.tag,
  });
  const { openCountry } = useGeographicDrawer({ entityTag: entity.tag });

  if (error && error.status !== httpCode.notFound) {
    throw new Error('Failed to load GeographicExposureKnowledgeGraph');
  }

  const topCountries = [...countryExposureData]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3);

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    /** i18next-extract-mark-context-next-line ["geographicExposure"] */
    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag: entity.tag,
    searchParams,
    subtab: KnowledgeGraphTabType.GeographicExposure,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  if (!isFetching && countryExposureData.length === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {t('widget:assetOverview.knowledgeGraph.subtab.geographic')}
        </div>
        <ConnectionsEmptyOrError
          variant="empty"
          title={t(
            'widget:assetOverview.knowledgeGraphRevamp.empty.geographicExposure'
          )}
          layout="fixed"
        />
      </div>
    );
  }

  return (
    <div>
      <SectionTitle
        title={t('widget:assetOverview.knowledgeGraph.subtab.geographic')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('geographicExposure')}
      />
      {isFetching ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <RowItem key={idx} rowType="country" isLoading />
          ))}
        </div>
      ) : (
        <div>
          {topCountries.map(country => {
            const countryName = t(`countries:${country.code}`, {
              defaultValue: country.code,
            });

            return (
              <RowItem
                key={country.code}
                rowType="country"
                rank={country.relationship.rank}
                onClick={() => openCountry(country.code)}
              >
                <FlagIcon
                  countryCode={country.code}
                  customSize={32}
                  variant="circular"
                />
                <span className={styles.countryName}>{countryName}</span>
              </RowItem>
            );
          })}
        </div>
      )}
      <GeographicExposureDrawer entity={entity} />
    </div>
  );
};
